import {sharedCampaigns} from '@cl/shared';
import {CAMPAIGN_NODE_ID, NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {updateCampaignNodes} from 'common/src/modules/campaigns/store/campaignLogic/actions/updateCampaignNodes';
import campaignVersionsApi, {
  PatchTreeParams,
} from 'common/src/modules/campaignVersions/campaignVersions.api';
import {Nodes} from 'common/src/modules/nodes/nodes.types';
import getCampaignNodes from 'common/src/modules/nodes/utils/getCampaignNodes';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import refreshCampaignIssues from '../../../utils/refreshCampaignIssues';
import {CardConnectionType} from './cardsConnectionTool.enums';
import {cardsConnectionToolSchema, ConnectionRequest} from './cardsConnectionTool.types';
import processConnection, {ConnectionResultStatus} from './utils/processConnection';

function cardsConnectionTool(store: CLStore) {
  return tool({
    name: CampaignSetupAgentToolName.CardsConnection,
    description: 'Connect or disconnect cards in the campaign decision tree',
    displayName: 'Updating map connections',
    inputSchema: cardsConnectionToolSchema,
    execute: async ({
      input: {connections},
    }: ToolExecuteParams<typeof cardsConnectionToolSchema>) => {
      const state = store.getState();
      const campaignLogics = state.campaignPage.campaignLogic;

      if (!campaignLogics?.nodes) {
        return toolError('No campaign setup data available');
      }

      const storeNodes = sharedCampaigns.nodesRef.value;
      const campaign = state.campaignPage?.campaignTree?.campaign;
      const versionNumber = state.campaignPage?.campaignTree?.versionNumber;

      if (!campaign || !versionNumber) {
        return toolError('Campaign or version number not found in state');
      }

      const nodesToUpdate: Nodes = {};
      const notes: string[] = [];
      let rootNodeId = NO_NODE_ID;
      let hasCampaignConnectionChanges = false;

      // Process each connection individually - validate and apply
      for (const connection of connections as ConnectionRequest[]) {
        const result = processConnection({connection, storeNodes, nodesToUpdate});

        if (result.message) {
          notes.push(result.message);
        }

        // Track campaign connection changes for rootNodeId
        if (
          result.status === ConnectionResultStatus.Success &&
          connection.sourceCardId === CAMPAIGN_NODE_ID
        ) {
          hasCampaignConnectionChanges = true;

          if (connection.type === CardConnectionType.Connect) {
            rootNodeId = connection.targetCardId;
          } else if (
            connection.type === CardConnectionType.Disconnect &&
            rootNodeId === connection.targetCardId
          ) {
            rootNodeId = NO_NODE_ID;
          }
        }
      }

      const hasCardUpdates = Object.keys(nodesToUpdate).length > 0;

      if (!hasCardUpdates && !hasCampaignConnectionChanges) {
        const message =
          notes.length > 0
            ? `No connections were updated.\n\nNotes:\n${notes.map((msg) => `* ${msg}`).join('\n')}`
            : 'No connections were updated';

        return toolError(message);
      }

      const basePayload: PatchTreeParams = {
        campaignId: campaign.id,
        versionNumber,
        nodesToAdd: [],
        nodesToDelete: [],
        nodesToUpdate: nodesToUpdate as Nodes,
        campaignNode: campaign,
        ...(hasCampaignConnectionChanges && {rootNodeId}),
      };

      const response = await campaignVersionsApi.updateTree(basePayload);

      const newNodes = getCampaignNodes(response);

      store.dispatch(
        updateCampaignNodes({
          connectedNodes: newNodes,
          campaignTreeType: CampaignTreeType.Campaign,
        }),
      );

      await refreshCampaignIssues(store);

      const baseMessage = 'Success';
      const fullMessage =
        notes.length > 0
          ? `${baseMessage}\n\nNotes:\n${notes.map((msg) => `* ${msg}`).join('\n')}`
          : baseMessage;

      return toolResult(
        JSON.stringify({
          success: true,
          message: fullMessage,
        }),
      );
    },
  });
}

export default cardsConnectionTool;
